import { useState, useEffect, useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth';
import { auth } from '../firebase/config';
import UsersService, { User } from '../../services/users';
import { UserRole } from '../types/shared';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  // Login function
  const login = useCallback(async (email: string, password: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const fbUser: FirebaseUser = userCredential.user;
      const userData = await UsersService.getUserById(fbUser.uid);
      
      if (!userData) {
        throw new Error('User data not found');
      }
      
      if (userData.status !== 'active') {
        throw new Error('Account is not active. Please contact support.');
      }
      
      setAuthState({ 
        user: userData, 
        isAuthenticated: true, 
        isLoading: false, 
        error: null 
      });
      
      return true;
    } catch (error) {
      setAuthState({ 
        user: null, 
        isAuthenticated: false, 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Login failed' 
      });
      return false;
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      await signOut(auth);
      setAuthState({ 
        user: null, 
        isAuthenticated: false, 
        isLoading: false, 
        error: null 
      });
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Logout failed'
      }));
    }
  }, []);

  // Registration function
  const register = useCallback(async (
    email: string,
    password: string,
    additionalData: { name: string; role: UserRole; metadata?: { timezone?: string; phoneNumber?: string } }
  ) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const fbUser: FirebaseUser = userCredential.user;
      
      try {
        const userData = {
          name: additionalData.name,
          email: fbUser.email || email,
          role: additionalData.role,
          status: 'active' as const,
          hierarchyPath: `/${fbUser.uid}`,
          emailVerified: true,
          metadata: additionalData.metadata || {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            phoneNumber: ''
          }
        };
        
        await UsersService.createUser(fbUser.uid, userData);
        const createdUser = await UsersService.getUserById(fbUser.uid);
        
        if (!createdUser) {
          throw new Error('Failed to fetch created user');
        }
        
        setAuthState({ 
          user: createdUser, 
          isAuthenticated: true, 
          isLoading: false, 
          error: null 
        });
        
        return true;
      } catch (error) {
        // Clean up the Auth user if Firestore creation fails
        await fbUser.delete();
        throw error;
      }
    } catch (error) {
      setAuthState({ 
        user: null, 
        isAuthenticated: false, 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Registration failed' 
      });
      return false;
    }
  }, []);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        try {
          const userData = await UsersService.getUserById(fbUser.uid);
          if (userData) {
            if (userData.status !== 'active') {
              await signOut(auth);
              setAuthState({ 
                user: null, 
                isAuthenticated: false, 
                isLoading: false, 
                error: 'Account is not active. Please contact support.' 
              });
            } else {
              setAuthState({ 
                user: userData, 
                isAuthenticated: true, 
                isLoading: false, 
                error: null 
              });
            }
          } else {
            setAuthState({ 
              user: null, 
              isAuthenticated: false, 
              isLoading: false, 
              error: 'User data not found' 
            });
          }
        } catch (error) {
          setAuthState({ 
            user: null, 
            isAuthenticated: false, 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Session validation failed' 
          });
        }
      } else {
        setAuthState({ 
          user: null, 
          isAuthenticated: false, 
          isLoading: false, 
          error: null 
        });
      }
    });

    return () => unsubscribe();
  }, []);

  return { ...authState, login, logout, register };
}

export default useAuth;
